import { Button } from "@/components/ui/button";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useFacilities } from "@/hooks/useFacilities";
import { useDispatch } from "react-redux";
import { translateWithMultipleSources } from "@/lib/translate";
import { AppLocation, FunnelType, QuoteType, Step } from "@/types/enums";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import { initializeJourney } from "@/features/journey/journeySlice";
import { UnitGroup } from "@/types";
import { getSteps } from "@/hooks/useSteps";

interface UnitQuoteButtonProps {
    unitGroup: UnitGroup;
    selectedPromo: number;
}

export const QuoteButton = ({
    unitGroup,
    selectedPromo,
}: UnitQuoteButtonProps) => {
    const dispatch = useDispatch();
    const options = useDisplayOptionContext();
    const { facilities } = useFacilities();

    const unitId = unitGroup.representative_unit.id;
    const facilityId = unitGroup.representative_unit.facility_id;

    const facilityInfo = facilities.find(
        (facility) => facility.id === facilityId,
    );

    const handleQuoteEvent = () => {
        emitUnitEvent(
            unitId,
            unitGroup.representative_unit.facility_id,
            selectedPromo,
            "inquiry",
        );
    };

    const handleExternalSoftReservation = () => {
        if (facilityInfo && options.softReservationApp?.externalUrl) {
            const translatedUrl = translateWithMultipleSources(
                options.softReservationApp.externalUrl,
                {
                    unitGroup,
                    facility: facilityInfo,
                },
                Number(unitId),
                Number(selectedPromo),
            );
            window.location.href = translatedUrl;
        }
    };

    const activateSoftReservationJourney = () => {
        const subGroupCount = unitGroup.sub_groups?.length ?? 0;
        const steps = getSteps(subGroupCount, options.buttonLayout);

        dispatch(
            initializeJourney({
                unitId,
                unitGroup,
                selectedPromo,
                stepList: steps,
                step: Step.FUNNEL,
                funnelType: FunnelType.QUOTE,
            }),
        );
    };

    const handleSoftReserveClick = () => {
        handleQuoteEvent();

        if (
            options.softReservationApp?.location === AppLocation.EXTERNAL &&
            options.softReservationApp?.externalUrl
        ) {
            handleExternalSoftReservation();
        } else {
            activateSoftReservationJourney();
        }
    };

    if (
        options.disableSoftReservation &&
        facilityInfo?.settings.quote_type !== QuoteType.RESERVE
    )
        return null;

    return (
        <Button
            variant="secondary"
            data-testid="quoteButton"
            onClick={handleSoftReserveClick}
            className="soft-reservation-btn"
        >
            {options.language?.action?.quoteButton}
        </Button>
    );
};

QuoteButton.displayName = "QuoteButton";
